import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog"
import { Button } from "@/components/button"
import { useStep } from "usehooks-ts"
import Step1 from "./step-1"
import Step2 from "./step-2"
import Step3 from "./step-3"
import { ChevronRight, Rocket, ChevronLeft } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useNoLifeAlert } from "@/modules/app/stores/use-no-life-alert"
import useCanContinueLearning from "@/modules/user/hooks/use-can-continue-learning"

interface ExplanatoryModalProps {
  isOpen: boolean
  closeModal: () => void
}

export default function ExplanatoryModal({ isOpen, closeModal }: ExplanatoryModalProps) {
  const navigate = useNavigate()
  const [currentStep, stepActions] = useStep(3)
  const { reset, canGoToNextStep, goToNextStep, goToPrevStep } = stepActions
  const canContinueLearning = useCanContinueLearning()
  const { setIsOpen: openNoLifeAlert } = useNoLifeAlert()

  const handleOnClose = () => {
    closeModal()
    reset()
  }

  const getStepComponent = () => {
    const Component = {
      1: <Step1 />,
      2: <Step2 />,
      3: <Step3 />,
    }[currentStep]

    return Component || null
  }

  const getButtonContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <span>Entender cómo funciona</span>
            <ChevronRight />
          </>
        )
      case 2:
        return (
          <>
            <span>Siguiente</span>
            <ChevronRight />
          </>
        )
      case 3:
        return (
          <>
            <Rocket />
            <span>Comenzar</span>
          </>
        )
      default:
        return null
    }
  }

  const handleOnClick = () => {
    if (canGoToNextStep) {
      goToNextStep()
    } else {
      if (!canContinueLearning) {
        openNoLifeAlert(true)
        return
      }

      navigate("/flashcards/special-deck")
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOnClose}>
      <DialogContent className="flex size-[98%] max-w-[450px] flex-col gap-5 overflow-hidden border-none bg-[#961E9D] text-white lg:max-h-[90vh]">
        <DialogHeader className="w-full shrink-0">
          <div className="flex w-full items-center justify-start">
            {currentStep !== 1 ? (
              <Button
                className="mt-[-7px] !size-4 bg-transparent p-0 text-white"
                onClick={() => goToPrevStep()}
              >
                <ChevronLeft />
              </Button>
            ) : null}
          </div>
        </DialogHeader>
        <div className="size-full overflow-hidden">{getStepComponent()}</div>
        <DialogFooter className="flex shrink-0 flex-row items-center justify-end gap-1 overflow-hidden lg:mt-14">
          <Button onClick={handleOnClick} className="w-full">
            <p className="m-0 flex items-center justify-center gap-2 p-0 text-white">
              {getButtonContent()}
            </p>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
